import React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatScore10 } from '../create/format';
import { COPY } from '../../contracts/copyMap';

// Score movement across recent readings, on the home screen.
//
// WHY IT EXISTS
// Home could show where you stand and whether the last reading moved, but not
// the shape of the change. A single delta chip answers "up or down since last
// time"; it cannot answer "am I actually getting anywhere", which is the
// question that decides whether someone comes back. 6.8% of users take a second
// scan, so the surface that rewards having taken several has to exist before
// the behaviour it rewards will.
//
// ON THE DEPENDENCY
// Recharts, at the project owner's direction. Lane C removed `@nivo/*`,
// `chart.js` and `react-chartjs-2`, so this re-introduces a class of dependency
// the cleanup deleted (+371KB raw on main.js) — it earns that with a hover
// readout, monotone curve interpolation and real axis layout, none of which the
// hand-rolled SVG had. Recharts specifically because it renders SVG (the rest
// of the design system is SVG and stays crisp at any density) and ships no
// visual opinion: every stroke, fill, tick and tooltip here is ours. A canvas
// library or a themed dashboard kit would have fought DESIGN.md instead of
// disappearing into it.
//
// STYLING NOTES (DESIGN.md)
//   · Aurum on the line and the active dot — the sanctioned "metric value" use.
//   · Marble/Bone for ticks, Vein for the grid: chrome never competes with data.
//   · Sharp corners on the tooltip, hairline border, no shadow — it is a chyron,
//     not a SaaS popover.
//   · monotone interpolation, not natural: a natural spline overshoots between
//     points and invents scores the model never produced.
//
// THE Y DOMAIN IS FLOOR-CLAMPED, DELIBERATELY. Fitting the axis tightly to the
// observed range makes a one-point wobble fill the full height of the chart and
// read as a transformation. `scoreDelta.js` already refuses to report movement
// under 0.05 for the same reason — the model is not precise enough for that to
// mean anything, and manufacturing a dramatic-looking climb is the same lie at
// a larger scale. MIN_SPAN keeps a flat run looking flat.
//
// Props:
//   entries    chronological ASCENDING, oldest first: [{ scan_id, overall_score, taken_at|timestamp }]
//   height     chart height in px (default 108; Home passes more on desktop)
//   className

const AURUM = '#C9A45C';
const AURUM_BRIGHT = '#E6C083';
const VEIN = '#2A2A2A';
const BONE_DIM = '#8A857C';

// Minimum y-axis span, on the raw 1-100 scale (= 1.0 on the displayed /10).
const MIN_SPAN = 10;
// Headroom above and below the observed range so end points are not on the edge.
const DOMAIN_PAD = 3;

// Four evenly spaced y ticks across the domain, on the raw 1-100 scale. Even
// spacing rather than round numbers: the domain is already floor-clamped and
// padded, so snapping ticks to multiples would either drift off the ends or
// collapse to two labels on a tight range.
export function buildTicks(min, max, count = 4) {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + step * i);
}

export function buildDomain(scores) {
  const lo = Math.min(...scores);
  const hi = Math.max(...scores);
  let min = lo - DOMAIN_PAD;
  let max = hi + DOMAIN_PAD;

  const span = max - min;
  if (span < MIN_SPAN) {
    const grow = (MIN_SPAN - span) / 2;
    min -= grow;
    max += grow;
  }
  // Never claim range the scale does not have.
  return { min: Math.max(0, min), max: Math.min(100, max) };
}

function fmtDate(iso) {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '';
  return new Date(ms)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    .toUpperCase();
}

// OUR OWN TICK RENDERER, and it is not cosmetic.
//
// Recharts draws tick labels through its internal <Text> component, and that
// component is the one thing in this library that does not survive this
// project's production build: webpack emits `isNumber(...)` inside Text.js as a
// bare, undeclared identifier, so the first axis label to render throws
// `ReferenceError: isNumber is not defined` and takes the whole page with it.
// It reproduces on recharts 2.15.4 and 3.10.1 alike, so it is the bundler, not
// the library — the project carries a direct `webpack@^5.97.1` devDependency
// and resolves to 5.99.3, far ahead of the ^5.64.4 CRA 5 was built against, and
// the mangling bug only bites on this code path.
//
// Passing an ELEMENT for `tick` makes Recharts render this instead of <Text>,
// which is a supported API and sidesteps the broken module entirely. Nothing
// else here touches it: the grid and reference line draw no text, and the
// tooltip is our own HTML. Changing the app's webpack version to fix one chart
// would repaint the production bundle for every other surface too.
//
// Recharts hands a tick `payload.value` plus the resolved x/y for its anchor.
// It does NOT run `tickFormatter` for a custom tick element — that only applies
// to its own <Text>. Formatting therefore happens here, or the y axis prints
// raw domain values like 70.33333333333333.
const AxisTick = ({ x, y, textAnchor = 'middle', dy = 0, payload, format }) => (
  <text
    x={x}
    y={y}
    dy={dy}
    textAnchor={textAnchor}
    fill={BONE_DIM}
    style={{ fontSize: 9.5, letterSpacing: '0.18em' }}
    className="font-display"
  >
    {payload ? format(payload.value) : ''}
  </text>
);

// y: the raw 1-100 domain, shown on the /10 scale the rest of the product uses.
// x: already a formatted date string from `fmtDate`.
const YAxisTick = (props) => (
  <AxisTick {...props} textAnchor="end" dy={3} format={formatScore10} />
);
const XAxisTick = (props) => <AxisTick {...props} dy={12} format={String} />;

// A chyron, not a SaaS popover: sharp corners, hairline border, no shadow.
const TrendTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div
      data-testid="trend-tooltip"
      className="border border-hairline-soft bg-obsidian px-3 py-2"
      style={{ minWidth: 92 }}
    >
      <div
        className="font-display tabular-nums leading-none"
        style={{ color: AURUM_BRIGHT, fontSize: 18 }}
      >
        {formatScore10(p.score)}
      </div>
      <div
        className="mt-1 font-display uppercase tracking-[0.18em]"
        style={{ color: BONE_DIM, fontSize: 9 }}
      >
        {p.label}
      </div>
    </div>
  );
};

// Hollow dots along the series, a filled one on the newest reading — the eye
// should land on "where you are now" before it traces how you got here.
const TrendDot = ({ cx, cy, index, dataLength }) => {
  if (cx == null || cy == null) return null;
  const isLast = index === dataLength - 1;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={isLast ? 4 : 2.4}
      fill={isLast ? AURUM_BRIGHT : '#0d0c0b'}
      stroke={AURUM}
      strokeWidth={1.2}
    />
  );
};

const ScoreTrend = ({ entries, height = 108, className = '' }) => {
  // `typeof === 'number'`, not `Number.isFinite(Number(x))` — the latter accepts
  // null (Number(null) === 0) and '', so a scan whose score failed to persist
  // would plot at zero and crater the whole series.
  const points = (entries || []).filter(
    (e) => e && typeof e.overall_score === 'number' && Number.isFinite(e.overall_score),
  );

  if (points.length === 0) return null;

  const data = points.map((e, i) => ({
    key: e.scan_id || String(i),
    score: e.overall_score,
    label: fmtDate(e.taken_at || e.timestamp),
  }));

  const { min, max } = buildDomain(data.map((d) => d.score));
  const yTicks = buildTicks(min, max);
  const xTicks = [...new Set([data[0].label, data[data.length - 1].label])];
  const single = data.length === 1;

  return (
    <div
      data-testid="score-trend"
      data-single={single ? 'true' : undefined}
      className={className}
      role="img"
      aria-label={
        single
          ? `One reading, ${formatScore10(data[0].score)} out of 10. A second reading is needed to show movement.`
          : `Score trend across ${data.length} readings, from ${formatScore10(
              data[0].score,
            )} to ${formatScore10(data[data.length - 1].score)} out of 10`
      }
    >
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id="scoreTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={AURUM} stopOpacity={0.26} />
              <stop offset="100%" stopColor={AURUM} stopOpacity={0} />
            </linearGradient>
          </defs>

          {/* Both axes now. Verticals were left off originally to avoid the
              dashboard-grid look, but with only two x labels the series had
              nothing to sit against and the readings read as unplaced. Kept to
              a 1-4 dash at Vein so it stays a substrate, not a cage. */}
          <CartesianGrid stroke={VEIN} strokeDasharray="1 4" />

          <XAxis
            dataKey="label"
            axisLine={{ stroke: VEIN }}
            tickLine={{ stroke: VEIN }}
            tick={<XAxisTick />}
            // Only the ends. Every reading's date would collide at 375px, and
            // the middle labels are what the tooltip is for.
            //
            // Deduped: with one reading the first and last ARE the same point,
            // and passing the label twice made Recharts render two ticks with
            // an identical React key. Two readings taken the same day collide
            // the same way.
            ticks={xTicks}
            interval="preserveStartEnd"
          />
          {/* Ticks on the /10 scale the rest of the product shows, so a reading
              can be placed without hovering it. */}
          <YAxis
            domain={[min, max]}
            ticks={yTicks}
            axisLine={false}
            tickLine={{ stroke: VEIN }}
            tick={<YAxisTick />}
            // interval=0 renders every tick we asked for. Recharts drops labels
            // it believes would collide, and with a custom tick element it
            // cannot measure the text to decide — left to itself it silently
            // showed two of the four on some ranges and all four on others.
            interval={0}
            width={34}
          />

          <Tooltip
            content={<TrendTooltip />}
            cursor={{ stroke: AURUM, strokeWidth: 1, strokeDasharray: '3 3', strokeOpacity: 0.5 }}
          />

          {/* Where the series began, so a climb is legible as a climb. With one
              reading it is the only horizontal reference there is. */}
          <ReferenceLine y={data[0].score} stroke={VEIN} strokeDasharray="3 3" />

          <Area
            type="monotone"
            dataKey="score"
            stroke={AURUM}
            strokeWidth={1.75}
            fill="url(#scoreTrendFill)"
            dot={<TrendDot dataLength={data.length} />}
            activeDot={{ r: 5, fill: AURUM_BRIGHT, stroke: '#0d0c0b', strokeWidth: 2 }}
            // OFF, deliberately. Recharts reveals the series by animating a
            // clipPath rect from zero width to full, and in this build that rect
            // never grows past ~0.3px: every path is present and correct in the
            // DOM and the chart is invisible on screen. Gating the mount on a
            // measured container did not help, so the animation is the thing
            // that goes. A chart that renders beats a chart that draws itself in.
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>

      {/* A single reading still gets the chart — the axes, the grid and the dot
          show WHERE that score sits, which the hero's number alone does not, and
          an empty panel on the surface built to reward returning is the wrong
          thing to show someone one scan away from it. The note says plainly what
          is missing rather than leaving a flat chart to imply no progress. */}
      {single && (
        <p
          data-testid="trend-needs-second"
          className="m-0 mt-2 font-display italic text-bone-dim"
          style={{ fontSize: 13, lineHeight: 1.5 }}
        >
          {COPY.HOME_TREND_NEEDS_SECOND}
        </p>
      )}
    </div>
  );
};

export default ScoreTrend;
